import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Logo from '../components/Logo';
import BackgroundLayout from '../components/BackgroundLayout';

const ThankYouPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // UUID de la cita: es el mismo valor que codifica el QR del correo de
    // confirmación y el que lee el escáner del staff (confirm-attendance).
    const appointmentId = searchParams.get('cita');

    useEffect(() => {
        // Si mostramos el QR no redirigimos: la persona necesita tiempo para
        // capturarlo (sobre todo si no le llegó el correo).
        if (appointmentId) return;

        const timer = setTimeout(() => {
            navigate('/services');
        }, 15000); // Redirige después de 10 segundos

        return () => clearTimeout(timer); // Limpia el temporizador si el componente se desmonta
    }, [navigate, appointmentId]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3, // Aumenta el tiempo entre la aparición de cada hijo
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 1, // Aumenta la duración de la animación
                ease: "easeInOut", // Suaviza la animación
            },
        },
        exit: {
            opacity: 0,
            y: 20,
            transition: {
                duration: 1, // Aumenta la duración de la animación de salida
                ease: "easeInOut", // Suaviza la animación de salida
            },
        },
    };

    const draw = {
        hidden: { pathLength: 0, opacity: 0 },
        visible: (i) => {
            const delay = 1 + i * 0.5;
            return {
                pathLength: 1,
                opacity: 1,
                transition: {
                    pathLength: { delay, type: "spring", duration: 1.5, bounce: 0 },
                    opacity: { delay, duration: 0.01 }
                }
            };
        }
    };

    return (
        <BackgroundLayout className="px-8 overflow-hidden items-center justify-center">
            <motion.div
                className="w-full flex flex-col items-center text-center max-w-lg mb-20"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <Logo />
                
                {/* Modern Success Animation */}
                <div className="relative mt-12 mb-8 flex items-center justify-center">
                    <motion.div 
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.5 }}
                        className="w-32 h-32 rounded-full border-8 border-[#f8485e]/10 flex items-center justify-center bg-white shadow-[0_0_50px_rgba(248,72,94,0.1)] relative"
                    >
                        <motion.svg
                            width="60"
                            height="60"
                            viewBox="0 0 60 60"
                            initial="hidden"
                            animate="visible"
                        >
                            <motion.path
                                d="M12 30 L24 42 L48 18"
                                fill="transparent"
                                strokeWidth="8"
                                stroke="#f8485e"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                variants={draw}
                                custom={1}
                            />
                        </motion.svg>
                        
                        {/* Decorative pulses */}
                        <motion.div 
                            animate={{ scale: [1, 1.4, 1], opacity: [0.1, 0, 0.1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute inset-0 rounded-full bg-[#f8485e]"
                        />
                    </motion.div>
                </div>

                <motion.h1 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.8 }} 
                    className='text-6xl font-black text-[#f8485e] uppercase tracking-tighter drop-shadow-sm'
                >
                    ¡LISTO! ✨
                </motion.h1>

                <motion.div 
                    variants={itemVariants}
                    className="mt-6 p-6 bg-white/50 backdrop-blur-md border border-white/50 rounded-3xl shadow-xl border-b-4 border-b-[#f8485e]/20"
                >
                    <p className="text-xl font-bold text-gray-800 uppercase tracking-tight leading-7">
                        {appointmentId
                            ? 'Tu cita se registró con éxito. Toma captura de este código y preséntalo al staff de Inspira al llegar.'
                            : 'Tu cita se registró con éxito. Revisa tu correo de confirmación, lo necesitarás al llegar a Inspira.'}
                    </p>
                </motion.div>

                {/* QR de la cita: respaldo por si el correo no llega */}
                {appointmentId && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.2, duration: 0.6, ease: 'easeOut' }}
                        className="mt-6 flex flex-col items-center"
                    >
                        <QRCodeSVG
                            className="border-8 rounded-2xl border-white shadow-2xl"
                            value={appointmentId}
                            size={250}
                        />
                        <p className="mt-4 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">
                            También te lo enviamos por correo 💌
                        </p>
                    </motion.div>
                )}

                {/* Disclaimer - Gestión de citas */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2, duration: 0.6, ease: 'easeOut' }}
                    className="mt-4 w-full"
                >
                    <div className="flex items-start gap-3 bg-white/60 backdrop-blur-md border border-[#f8485e]/20 rounded-2xl px-5 py-4 shadow-sm text-left">
                        <span className="text-[#f8485e] text-lg mt-0.5 flex-shrink-0">✉️</span>
                        <div>
                            <p className="text-sm font-bold text-gray-800">¿Quieres cambiar tu cita?</p>
                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">Para cancelar o reagendar, usa el enlace que llegó a tu correo de confirmación. Es la única forma segura de hacerlo.</p>
                        </div>
                    </div>
                </motion.div>

                {appointmentId ? (
                    <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 3 }}
                        onClick={() => navigate('/services')}
                        className="mt-10 p-3 bg-inspiraPink text-white rounded-md drop-shadow-md w-2/3 font-bold uppercase"
                    >
                        Volver a servicios
                    </motion.button>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 3 }}
                        className="mt-10 flex items-center gap-2 text-[#f8485e]/60 font-black text-[10px] uppercase tracking-[0.3em]"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#f8485e] animate-pulse" />
                        Redirigiendo a servicios
                    </motion.div>
                )}
            </motion.div>
        </BackgroundLayout>
    );
};

export default ThankYouPage;